import React, { useEffect, useState } from "react";
import { api, formatDateTime } from "../lib/api";
import { useAuth } from "../lib/auth";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { Send, Inbox, FolderOpen, Loader2, CheckCircle2, X } from "lucide-react";

const STATUS_TABS = [
    { key: "new", label: "New", icon: Inbox },
    { key: "open", label: "Open", icon: FolderOpen },
    { key: "in_progress", label: "In Progress", icon: Loader2 },
    { key: "resolved", label: "Resolved", icon: CheckCircle2 },
    { key: "closed", label: "Closed", icon: X },
];

const Tickets = () => {
    const { user } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [buckets, setBuckets] = useState({});
    const [activeTab, setActiveTab] = useState("new");
    const [selected, setSelected] = useState(null);
    const [reply, setReply] = useState("");
    const [sending, setSending] = useState(false);

    const loadBuckets = () => api.get("/tickets/buckets").then((r) => setBuckets(r.data));
    const loadList = (status) => api.get(`/tickets?status=${status}`).then((r) => setTickets(r.data));

    useEffect(() => { loadBuckets(); }, []);
    useEffect(() => { loadList(activeTab); setSelected(null); }, [activeTab]);

    const openTicket = async (t) => {
        const r = await api.get(`/tickets/${t.id}`);
        setSelected(r.data);
        setReply("");
    };

    const sendReply = async () => {
        if (!reply.trim() || !selected) return;
        setSending(true);
        try {
            const r = await api.post(`/tickets/${selected.id}/replies`, { message: reply });
            setSelected(r.data);
            setReply("");
            loadList(activeTab);
            loadBuckets();
        } finally {
            setSending(false);
        }
    };

    const moveStatus = async (status) => {
        if (!selected) return;
        const r = await api.patch(`/tickets/${selected.id}`, { status });
        setSelected(r.data);
        loadList(activeTab);
        loadBuckets();
    };

    return (
        <div className="space-y-6">
            <PageHeader title="Support Tickets" subtitle="Reply to riders & drivers, move tickets across buckets" />

            {/* Bucket tabs */}
            <div className="bg-white border border-slate-200 rounded-2xl p-2 grid grid-cols-2 md:grid-cols-5 gap-1">
                {STATUS_TABS.map(({ key, label, icon: Icon }) => (
                    <button
                        key={key}
                        onClick={() => setActiveTab(key)}
                        className={`flex items-center justify-center gap-2 px-3 py-2 rounded-full text-sm font-semibold transition-colors ${
                            activeTab === key
                                ? "bg-mint-500 text-white"
                                : "text-slate-600 hover:bg-slate-50"
                        }`}
                        data-testid={`tickets-tab-${key}`}
                    >
                        <Icon className="w-4 h-4" />
                        {label}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${activeTab === key ? "bg-white/20" : "bg-slate-100 text-slate-600"}`}>
                            {buckets[key] || 0}
                        </span>
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* List */}
                <div className="lg:col-span-1 space-y-2 max-h-[70vh] overflow-y-auto">
                    {tickets.length === 0 ? (
                        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-slate-400 text-sm">
                            No tickets in this bucket.
                        </div>
                    ) : tickets.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => openTicket(t)}
                            className={`w-full text-left bg-white border rounded-2xl p-4 transition-colors ${
                                selected?.id === t.id ? "border-mint-400 ring-2 ring-mint-100" : "border-slate-200 hover:border-mint-200"
                            }`}
                            data-testid={`ticket-card-${t.id}`}
                        >
                            <div className="flex items-center justify-between mb-1">
                                <span className="font-mono text-[11px] text-slate-500">{t.id}</span>
                                <StatusBadge status={t.priority} />
                            </div>
                            <div className="font-display font-semibold text-navy-900 text-sm">{t.subject}</div>
                            <div className="text-xs text-slate-500 mt-1 line-clamp-2">{t.message}</div>
                            <div className="text-[11px] text-slate-400 mt-2">{t.customer_name || "—"} · {formatDateTime(t.created_at)}</div>
                            {t.replies && t.replies.length > 0 && (
                                <div className="text-[11px] text-mint-700 mt-1 font-semibold">{t.replies.length} repl{t.replies.length > 1 ? "ies" : "y"}</div>
                            )}
                        </button>
                    ))}
                </div>

                {/* Detail + reply */}
                <div className="lg:col-span-2">
                    {!selected ? (
                        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400 text-sm">
                            Select a ticket on the left to view & reply.
                        </div>
                    ) : (
                        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                            <div className="p-5 border-b border-slate-100">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="font-mono text-xs text-slate-500">{selected.id}</span>
                                    <StatusBadge status={selected.status} />
                                    <StatusBadge status={selected.priority} />
                                </div>
                                <h2 className="font-display font-bold text-xl text-navy-900" data-testid="ticket-subject">{selected.subject}</h2>
                                <div className="text-xs text-slate-500 mt-1">{selected.customer_name} · {formatDateTime(selected.created_at)}</div>
                            </div>

                            {/* Thread */}
                            <div className="p-5 space-y-3 max-h-[420px] overflow-y-auto bg-slate-50/30">
                                <div className="flex">
                                    <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-md px-4 py-3 max-w-xl">
                                        <div className="text-[11px] font-semibold text-slate-500 mb-1">{selected.customer_name || "Customer"}</div>
                                        <div className="text-sm text-navy-900">{selected.message}</div>
                                        <div className="text-[10px] text-slate-400 mt-1.5">{formatDateTime(selected.created_at)}</div>
                                    </div>
                                </div>
                                {(selected.replies || []).map((rep) => (
                                    <div key={rep.id} className={`flex ${rep.is_admin ? "justify-end" : ""}`}>
                                        <div className={`rounded-2xl px-4 py-3 max-w-xl ${
                                            rep.is_admin
                                                ? "bg-mint-500 text-white rounded-tr-md"
                                                : "bg-white border border-slate-200 rounded-tl-md"
                                        }`}>
                                            <div className={`text-[11px] font-semibold mb-1 ${rep.is_admin ? "text-mint-100" : "text-slate-500"}`}>
                                                {rep.author_name} {rep.is_admin && "· YuGo Team"}
                                            </div>
                                            <div className="text-sm">{rep.message}</div>
                                            <div className={`text-[10px] mt-1.5 ${rep.is_admin ? "text-mint-100/80" : "text-slate-400"}`}>{formatDateTime(rep.created_at)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Reply box */}
                            <div className="p-5 border-t border-slate-100">
                                <textarea
                                    value={reply}
                                    onChange={(e) => setReply(e.target.value)}
                                    placeholder="Reply to the customer…"
                                    rows={3}
                                    className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-mint-500/30 resize-none"
                                    data-testid="ticket-reply-input"
                                />
                                <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                                    <select
                                        value={selected.status}
                                        onChange={(e) => moveStatus(e.target.value)}
                                        className="px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg font-semibold"
                                        data-testid="ticket-move-status"
                                    >
                                        {STATUS_TABS.map((s) => (
                                            <option key={s.key} value={s.key}>Move to: {s.label}</option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={sendReply}
                                        disabled={!reply.trim() || sending}
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-mint-500 hover:bg-mint-600 text-white text-sm font-semibold disabled:opacity-50"
                                        data-testid="ticket-reply-send"
                                    >
                                        <Send className="w-4 h-4" />
                                        {sending ? "Sending…" : "Send reply"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Tickets;
